import { Box, Typography } from '@mui/material';
import React from 'react';

const Help = () => (
  <Box sx={{ padding: '20px', height: 'calc(100vh - 48px)', overflow: 'auto' }}>
    <Typography variant="h4" gutterBottom>
      Invoice Processing Website User Manual
    </Typography>

    <Typography variant="h6" gutterBottom>
      1. Introduction
    </Typography>

    <Typography variant="h6" gutterBottom>
      Overview
    </Typography>
    <Typography paragraph>
      Welcome to the Invoice Processing Website! This platform allows users to
      manage invoices efficiently, providing a seamless experience for creating,
      viewing, editing, and deleting invoices.
    </Typography>

    <Typography variant="h6" gutterBottom>
      Target Audience
    </Typography>
    <Typography paragraph>
      This manual is designed for all users of the Invoice Processing Website,
      including clients, administrative staff, and managers.
    </Typography>

    <Typography variant="h6" gutterBottom>
      Requirements
    </Typography>
    <Typography paragraph>
      Supported Browsers: Chrome, Firefox, Safari, Edge
    </Typography>
    <Typography paragraph>Internet Connection</Typography>
    <Typography paragraph>A valid account (for registered users)</Typography>
    <Typography variant="h6" gutterBottom>
      2. Getting Started
    </Typography>

    <Typography variant="h6" gutterBottom>
      Accessing the Website
    </Typography>
    <Typography paragraph>
      URL: <a href="https://yourwebsite.com">https://yourwebsite.com</a>
    </Typography>
    <Typography paragraph>
      Login: Enter your username and password to log in.
    </Typography>
    <Typography paragraph>
      Sign-Up: Click &quot;Sign Up&quot; on the homepage and fill in the
      required details to create a new account.
    </Typography>

    <Typography variant="h6" gutterBottom>
      Dashboard Overview
    </Typography>
    <Typography paragraph>
      After logging in, you&apos;ll see the dashboard, which includes:
      <li>
        Navigation Bar: Located at the top or side of the screen, providing
        quick access to different sections.
      </li>
      <li>
        Main Panel: Displays information and actions related to the selected
        section.
      </li>
    </Typography>

    <Typography variant="h6" gutterBottom>
      3. User Roles and Permissions
    </Typography>

    <Typography variant="h6" gutterBottom>
      Admin
    </Typography>
    <Typography paragraph>
      Full access to all features, including user management, settings, and
      reports.
    </Typography>

    <Typography variant="h6" gutterBottom>
      User
    </Typography>
    <Typography paragraph>
      Access to create, view, and manage invoices.
    </Typography>

    <Typography variant="h6" gutterBottom>
      Guest
    </Typography>
    <Typography paragraph>
      Limited access, primarily to view public information.
    </Typography>

    <Typography variant="h6" gutterBottom>
      4. Navigating the Website
    </Typography>

    <Typography variant="h6" gutterBottom>
      Main Menu
      <Typography>
        <li>Home: Overview of recent activities and notifications.</li>
        <li>Invoices: Access to all invoice-related features.</li>
        <li>Reports: Generate and view financial reports.</li>
        <li>Settings: Update personal and account settings.</li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      Subsections
    </Typography>

    <Typography variant="subtitle1" gutterBottom>
      Home
    </Typography>
    <Typography paragraph>
      Displays recent activities, notifications, and a summary of outstanding
      invoices.
    </Typography>

    <Typography variant="subtitle1" gutterBottom>
      Invoices
      <Typography component="div">
        <li>All Invoices: List of all invoices.</li>
        <li>Create Invoice: Form to create a new invoice.</li>
        <li>Invoice Search: Search bar to find specific invoices.</li>
      </Typography>
    </Typography>

    <Typography variant="subtitle1" gutterBottom>
      Reports
      <Typography component="div">
        <li>Monthly Reports: Financial summaries for each month.</li>
        <li>Custom Reports: Generate reports based on specific criteria.</li>
      </Typography>
    </Typography>

    <Typography variant="subtitle1" gutterBottom>
      Settings
      <Typography component="div">
        <li>Profile: Update your personal information.</li>
        <li>Account: Manage password and security settings.</li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      5. Invoice Management
    </Typography>

    <Typography variant="h6" gutterBottom>
      Creating an Invoice
      <Typography component="div">
        <li>Navigate to the Invoices section.</li>
        <li>Click Create Invoice.</li>
        <li>
          Fill in the required fields:
          <li>Customer Name</li>
          <li>Invoice Number</li>
          <li>Date</li>
          <li>Item Details (Description, Quantity, Price)</li>
          <li>Tax and Discounts (if applicable)</li>
        </li>
        <li>Click Save to create the invoice.</li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      Editing an Invoice
      <Typography component="div">
        <li>Go to the Invoices section.</li>
        <li>Click on the invoice you want to edit.</li>
        <li>Click the Edit button.</li>
        <li>Update the necessary information and click Save.</li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      Deleting an Invoice
      <Typography component="div">
        <li>Navigate to the Invoices section.</li>
        <li>Select the invoice you wish to delete.</li>
        <li>Click the Delete button and confirm the action.</li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      Viewing Invoice Details
      <Typography component="div">
        <li>Go to the Invoices section.</li>
        <li>
          Click on any invoice to view its details, including item breakdown,
          total amount, and status.
        </li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      6. Search and Filter
    </Typography>

    <Typography variant="h6" gutterBottom>
      Using Search
      <Typography component="div">
        <li>Navigate to the Invoices section.</li>
        <li>
          Use the search bar at the top to enter keywords (e.g., invoice number,
          customer name).
        </li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      Applying Filters
      <Typography component="div">
        <li>In the Invoices section, click on the Filter button.</li>
        <li>Select criteria such as date range, status, or amount.</li>
        <li>Click Apply to filter the list of invoices.</li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      7. Reports and Analytics
    </Typography>

    <Typography variant="h6" gutterBottom>
      Generating Monthly Reports
      <Typography component="div">
        <li>Go to the Reports section.</li>
        <li>Select Monthly Reports.</li>
        <li>Choose the desired month and year.</li>
        <li>Click Generate to view the report.</li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      Custom Reports
      <Typography component="div">
        <li>In the Reports section, click Custom Reports.</li>
        <li>Specify the criteria (e.g., date range, specific customers).</li>
        <li>Click Generate to view the report.</li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      8. User Settings and Profile
    </Typography>

    <Typography variant="h6" gutterBottom>
      Updating Profile Information
      <Typography component="div">
        <li>Go to the Settings section.</li>
        <li>Select Profile.</li>
        <li>Update the desired fields (name, email, etc.).</li>
        <li>Click Save.</li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      Changing Password
      <Typography component="div">
        <li>In the Settings section, select Account.</li>
        <li>Click Change Password.</li>
        <li>Enter the current password, then the new password.</li>
        <li>Confirm the new password and click Save.</li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      9. Notifications and Alerts
    </Typography>

    <Typography paragraph>
      Notifications are displayed at the top of the dashboard and alert users
      about new invoices, updates, and system messages.
    </Typography>

    <Typography variant="h6" gutterBottom>
      Managing Notifications
      <Typography component="div">
        <li>Click on the notification icon in the navigation bar.</li>
        <li>View and manage notifications from the dropdown list.</li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      10. Help and Support
    </Typography>

    <Typography variant="h6" gutterBottom>
      Contact Support
      <Typography component="div">
        <li>Email: support@yourwebsite.com</li>
        <li>Phone: 123-456-7890</li>
        <li>Live Chat: Available on the website during business hours.</li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      FAQs
    </Typography>
    <Typography paragraph>
      Visit the Help section on the website for frequently asked questions and
      troubleshooting tips.
    </Typography>

    <Typography variant="h6" gutterBottom>
      11. Troubleshooting
    </Typography>

    <Typography variant="h6" gutterBottom>
      Common Issues
      <Typography component="div">
        <li>
          Cannot Log In: Ensure your username and password are correct. Reset
          your password if necessary.
        </li>
        <li>
          Invoice Not Saving: Check for required fields and internet connection.
        </li>
        <li>
          Slow Performance: Clear browser cache and ensure your internet
          connection is stable.
        </li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      12. Glossary
      <Typography component="div">
        <li>
          <b>Invoice:</b> A document that itemizes and records a transaction
          between a buyer and a seller.
        </li>
        <li>
          <b>Dashboard:</b> The main interface providing an overview of
          activities and notifications.
        </li>
        <li>
          <b>Filter:</b> A tool used to sort and display specific data based on
          criteria.
        </li>
      </Typography>
    </Typography>

    <Typography variant="h6" gutterBottom>
      13. Appendices
    </Typography>

    <Typography variant="h6" gutterBottom>
      API Documentation
    </Typography>
    <Typography paragraph>
      For developers, detailed API documentation is available at{' '}
      <a href="/">https://yourwebsite.com/api-docs</a>.
    </Typography>
  </Box>
);

export default Help;
